import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { SelectField, PROMPT } from '@platform/ui-kit';
import { pdApi } from '../api';
import { HelpPanel, type HelpContent } from '../HelpPanel';
import './ExecutiveDashboard.css';

/**
 * INVENTORY REGISTRY — EXECUTIVE DASHBOARD.
 *
 * NEW WORK from a prototype the owner approved (2026-09-30). Not a port: the source app has
 * no equivalent screen, so §1.2's fidelity rule does not apply and the design is the one that
 * was signed off.
 *
 * INVENTORY REGISTRY DATA ONLY — that is what separates it from the Project Management
 * dashboard. Opportunities and their business case, the project they belong to, the
 * assessment framework and its dimension scores, the recommendation bands, and the financial
 * model where one exists. Nothing from Project Management.
 *
 * THE SCREEN DOES NOT SCROLL. Heading, project field, filters and tabs are fixed; the rail
 * and the analysis column scroll independently. Height is measured, never `100%` — see the
 * block in the component.
 */

/* ── Wire types ─────────────────────────────────────────────────────────────────────── */

interface ProjectRow {
  project_id: string; project_description: string; status: string;
  budget: number; project_owner: string; methodology: string;
}
interface OppRow {
  opportunity_id: string; description: string; project_id: string; status: string;
  category: string; delivery_pathway: string; framework_relevant: boolean; framework_id: string;
  overall_score: number | null; recommendation: string; business_benefit: number;
  estimated_delivery_cost: number; opportunity_budget: number;
  roi_score: number | null; criticality_score: number | null; benefit_cost_score: number | null;
  delivery_risk_score: number | null; ai_alignment_score: number | null; mvp_value_score: number | null;
  npv_value: number | null; npv_classification: string;
  start_date: string | null; end_date: string | null; l1_name: string;
  fm_build_cost: number | null; fm_mvp_build_cost: number | null; fm_annual_savings: number | null;
  fm_current_run_rate: number | null; fm_future_run_rate: number | null;
  fm_hosting: number | null; fm_support: number | null;
  fm_roi_percent: number | null; fm_roi_best: number | null; fm_roi_worst: number | null;
  fm_payback_years: number | null; fm_tco3: number | null; fm_tco5: number | null;
  has_model: boolean; model_npv: number | null; model_annual_benefit: number | null; has_npv: boolean;
}
interface DimRow { opportunity_id: string; dimension_name: string; dimension_score: number }
interface BandRow { label: string; min_score: number; max_score: number; color_code: string }
interface Payload {
  success: boolean; error?: string;
  projects: ProjectRow[]; opportunities: OppRow[]; dimensions: DimRow[]; bands: BandRow[];
}

/* ── Helpers ────────────────────────────────────────────────────────────────────────── */

const num = (v: unknown) => (v === null || v === undefined ? 0 : Number(v));
const money = (v: number) =>
  Math.abs(v) >= 1_000_000 ? '$' + (v / 1_000_000).toFixed(Math.abs(v) >= 10_000_000 ? 0 : 1) + 'M'
  : Math.abs(v) >= 1000 ? '$' + Math.round(v / 1000) + 'k'
  : '$' + Math.round(v);
const pctTxt = (v: number) => Math.round(v) + '%';
const sum = <T,>(a: T[], f: (x: T) => number) => a.reduce((t, x) => t + f(x), 0);
const clip = (s: string, n: number) => (s.length > n ? s.slice(0, n - 1) + '…' : s);
const esc = (v: unknown) => (v === null || v === undefined ? '' : String(v));

/*
 * MOST OF A REGISTRY IS NOT ASSIGNED TO A PROJECT, and it is still the portfolio.
 *
 * Opportunities carry `project_id` only once they are placed in one; a local install had
 * 47 of 51 sitting unassigned. Keying the project filter on real ids alone therefore hid
 * the bulk of the registry behind a picker that looked fully selected — the dashboard read
 * as empty while the data was all there. Unassigned work gets its own entry in the picker
 * so it is both visible and excludable, rather than silently dropped.
 */
const UNASSIGNED = '\u0000unassigned';

const HORIZON = 5;
const DISCOUNT = 0.1;

/** One opportunity, with its economics resolved once so every chart agrees. */
interface Opp {
  id: string; name: string; pid: string; status: string; l1: string;
  score: number | null; band: BandRow | null; relevant: boolean; pathway: string;
  benefit: number; cost: number; opex: number; net: number;
  roi: number; roiBest: number | null; roiWorst: number | null;
  payback: number | null; tco3: number; tco5: number; npv: number;
  hasModel: boolean; hasNpv: boolean; dims: Record<string, number>;
}

function build(p: Payload): Opp[] {
  const dimsBy: Record<string, Record<string, number>> = {};
  p.dimensions.forEach((d) => {
    (dimsBy[d.opportunity_id] ??= {})[d.dimension_name] = num(d.dimension_score);
  });
  const bandOf = (score: number | null) =>
    score == null ? null : p.bands.find((b) => score >= num(b.min_score) && score <= num(b.max_score)) ?? null;

  return p.opportunities.map((o) => {
    /*
     * WHICH FIGURE IS "BENEFIT", AND WHICH IS "COST".
     *
     * The financial model is authoritative where it exists — it is the worked case. Where it
     * does not, the business case on the opportunity is used, which is the only figure that
     * opportunity has. Preferring the model silently everywhere would make an unmodelled
     * opportunity read as worthless rather than as unmodelled.
     */
    const benefit = num(o.fm_annual_savings) || num(o.model_annual_benefit) || num(o.business_benefit);
    const cost = num(o.fm_build_cost) || num(o.estimated_delivery_cost) || num(o.opportunity_budget) || 0;
    const opex = num(o.fm_hosting) + num(o.fm_support);
    const net = benefit - opex;
    const payback = o.fm_payback_years != null ? num(o.fm_payback_years) : net > 0 && cost > 0 ? cost / net : null;
    const roi = o.fm_roi_percent != null ? num(o.fm_roi_percent) : cost > 0 ? (net / cost) * 100 : 0;
    let npv = o.model_npv != null ? num(o.model_npv) : o.npv_value != null ? num(o.npv_value) : NaN;
    if (Number.isNaN(npv)) {
      npv = -cost;
      for (let y = 1; y <= HORIZON; y++) npv += net / Math.pow(1 + DISCOUNT, y);
    }
    return {
      id: o.opportunity_id, name: o.description || o.opportunity_id, pid: o.project_id,
      status: o.status, l1: o.l1_name || 'Unclassified',
      score: o.overall_score == null ? null : num(o.overall_score),
      band: bandOf(o.overall_score == null ? null : num(o.overall_score)),
      relevant: Boolean(o.framework_relevant), pathway: o.delivery_pathway || 'B',
      benefit, cost, opex, net, roi,
      roiBest: o.fm_roi_best == null ? null : num(o.fm_roi_best),
      roiWorst: o.fm_roi_worst == null ? null : num(o.fm_roi_worst),
      payback,
      tco3: o.fm_tco3 != null ? num(o.fm_tco3) : cost + opex * 3,
      tco5: o.fm_tco5 != null ? num(o.fm_tco5) : cost + opex * 5,
      npv: Math.round(npv),
      hasModel: Boolean(o.has_model), hasNpv: Boolean(o.has_npv),
      dims: dimsBy[o.opportunity_id] ?? {},
    };
  });
}

/* ── Tooltip ────────────────────────────────────────────────────────────────────────── */

function useTip() {
  const [tip, setTip] = useState<{ html: ReactNode; x: number; y: number } | null>(null);
  const show = useCallback((e: { clientX: number; clientY: number }, html: ReactNode) =>
    setTip({ html, x: e.clientX, y: e.clientY }), []);
  const hide = useCallback(() => setTip(null), []);
  const node = tip ? (
    <div className="ir-exec-tip"
      style={{ left: Math.min(tip.x + 14, window.innerWidth - 290),
               top: Math.min(tip.y + 14, window.innerHeight - 170) }}>
      {tip.html}
    </div>
  ) : null;
  return { show, hide, node };
}
const R = ({ k, v }: { k: string; v: ReactNode }) => (
  <div className="r"><span className="k">{k}</span><span>{v}</span></div>
);
const tok = (root: HTMLElement | null, n: string) =>
  root ? getComputedStyle(root).getPropertyValue(n).trim() : '';

type Show = (e: { clientX: number; clientY: number }, h: ReactNode) => void;

/* ── Small pieces ───────────────────────────────────────────────────────────────────── */

/*
 * A CHART IS DRAWN AT THE WIDTH OF THE PANEL HOLDING IT, NEVER AT A CONSTANT.
 *
 * Every chart here is hand-drawn SVG, so its width used to be a number in the source —
 * 700 for the value map, 540 for the treemap. An SVG with a `width` attribute does not
 * grow: `max-width: 100%` lets it shrink and nothing more. On a wide monitor the panel
 * therefore ran to the edge of the screen and the drawing stopped two-thirds of the way
 * across, leaving the gap the owner reported.
 *
 * Nor can the fix be `width: 100%` with `height: auto`: the viewBox keeps the aspect
 * ratio, so a 700x420 plot on a 1580px column would be 948px TALL. The width is measured
 * and passed in instead, and a fixed-height chart grows its height sub-linearly (capped)
 * so a wide plot gets some vertical room without turning into a letterbox.
 *
 * The design numbers stay as the FALLBACK — they are what a chart draws at before the
 * panel has been measured, and outside a panel (the rail's gauges, the KPI sparklines).
 */
const PanelWidth = createContext<number>(0);

function useChartW(design: number): number {
  const w = useContext(PanelWidth);
  return w ? Math.max(300, w) : design;
}

function useChartBox(designW: number, designH: number): [number, number] {
  const W = useChartW(designW);
  const grow = Math.min(1.35, Math.max(1, (W / designW) ** 0.6));
  return [W, Math.round(designH * grow)];
}

function Panel({ eyebrow, title, note, children }:
  { eyebrow: string; title: string; note?: string; children: ReactNode }) {
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!box) return;
    const measure = () => {
      const cs = getComputedStyle(box);
      const pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      /* Two pixels short of the content box: an exact fit rounds into a scrollbar. */
      setW((prev) => {
        const next = Math.max(0, Math.floor(box.clientWidth - pad) - 2);
        return Math.abs(prev - next) < 2 ? prev : next;
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    return () => ro.disconnect();
  }, [box]);
  return (
    <section className="ix-panel">
      <header>
        <div className="eyebrow">{eyebrow}</div>
        <h2>{title}</h2>
        {note ? <p className="ix-note">{note}</p> : null}
      </header>
      <div className="ix-scroll" ref={setBox}>
        <PanelWidth.Provider value={w}>{children}</PanelWidth.Provider>
      </div>
    </section>
  );
}

function Gauge({ value, label, colour }: { value: number; label: string; colour: string }) {
  const size = 62, r = size / 2 - 7, c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, value));
  return (
    <div className="ix-gauge">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label={`${label} ${Math.round(v)}%`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5eaef" strokeWidth={7} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={colour} strokeWidth={7}
          strokeLinecap="round" strokeDasharray={`${(v / 100) * c} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        <text x={size / 2} y={size / 2 + 4} textAnchor="middle"
          style={{ font: "700 12.5px 'IBM Plex Mono',monospace", fill: '#14202a' }}>{Math.round(v)}%</text>
      </svg>
      <span className="lbl">{label}</span>
    </div>
  );
}

function MiniBars({ values, colour }: { values: number[]; colour: string }) {
  if (!values.length || !values.some((v) => v > 0)) return null;
  const W = 118, H = 26, gap = 3;
  const bw = (W - gap * (values.length - 1)) / values.length;
  const max = Math.max(...values) || 1;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-hidden="true">
      {values.map((v, i) => {
        const h = Math.max(v > 0 ? 2 : 0, (v / max) * (H - 3));
        return <rect key={i} x={i * (bw + gap)} y={H - h} width={bw} height={h} rx={1.5}
          fill={colour} fillOpacity={v > 0 ? 0.9 : 0.18} />;
      })}
    </svg>
  );
}

function Stack({ parts, empty }: { parts: { n: string; v: number; c: string }[]; empty?: string }) {
  const live = parts.filter((p) => p.v > 0);
  const total = sum(parts, (p) => p.v);
  if (!total) return <p className="ix-note" style={{ margin: 0 }}>{empty ?? 'Nothing recorded'}</p>;
  return (
    <>
      <div className="ix-stack">
        {live.map((p) => <i key={p.n} style={{ width: `${(p.v / total) * 100}%`, background: p.c }} />)}
      </div>
      <div className="ix-ft" style={{ borderTop: 0, marginTop: 9 }}>
        {live.map((p) => (
          <div className="ix-ft-row" key={p.n}>
            <span><i className="ix-sw" style={{ background: p.c, marginRight: 7 }} />{p.n}</span>
            <b>{p.v}</b>
          </div>
        ))}
      </div>
    </>
  );
}

/* ── Charts ─────────────────────────────────────────────────────────────────────────── */

/** THE VALUE MAP — cost against benefit, sized by NPV, coloured by band, payback diagonals. */
function ValueMap({ opps, root, show, hide }: { opps: Opp[]; root: HTMLElement | null; show: Show; hide: () => void }) {
  const [W, H] = useChartBox(700, 420);
  const live = opps.filter((o) => o.cost > 0 || o.benefit > 0);
  if (!live.length) return <div className="ix-empty">No opportunity in this selection carries a cost or a benefit yet.</div>;
  const L = 64, Rr = 24, T = 20, B = 52, iw = W - L - Rr, ih = H - T - B;
  const maxC = Math.max(...live.map((o) => o.cost)) * 1.1 || 1;
  const maxB = Math.max(...live.map((o) => o.benefit)) * 1.1 || 1;
  const x = (v: number) => L + (v / maxC) * iw;
  const y = (v: number) => T + (1 - v / maxB) * ih;
  const maxN = Math.max(...live.map((o) => Math.abs(o.npv))) || 1;
  const copper = tok(root, '--ix-copper');
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
        {[0, 1, 2, 3, 4].map((q) => (
          <g key={q}>
            <line x1={L} x2={L + iw} y1={y((maxB * q) / 4)} y2={y((maxB * q) / 4)} stroke={tok(root, '--ix-line-soft')} strokeWidth={1} />
            <text x={L - 8} y={y((maxB * q) / 4) + 3.5} textAnchor="end"
              style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-muted') }}>{money((maxB * q) / 4)}</text>
            <text x={x((maxC * q) / 4)} y={H - 28} textAnchor="middle"
              style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-muted') }}>{money((maxC * q) / 4)}</text>
          </g>
        ))}
        {[[1, '1-YEAR PAYBACK'], [2, '2 YEARS'], [3, '3 YEARS']].map(([yr, label], i) => {
          const x2 = Math.min(maxC, maxB * (yr as number));
          const y2 = x2 / (yr as number);
          return (
            <g key={String(yr)}>
              <line x1={x(0)} y1={y(0)} x2={x(x2)} y2={y(y2)} stroke={copper}
                strokeWidth={i ? 1 : 1.5} strokeDasharray={i ? '3 4' : '6 4'} strokeOpacity={i ? 0.5 : 1} />
              <text x={x(x2) - 4} y={y(y2) - 6} textAnchor="end" opacity={i ? 0.6 : 1}
                style={{ font: "600 9.5px 'IBM Plex Mono',monospace", fill: copper, letterSpacing: '.06em' }}>{label}</text>
            </g>
          );
        })}
        <text x={L + iw / 2} y={H - 9} textAnchor="middle"
          style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-ink-2') }}>BUILD COST →</text>
        <text x={14} y={T + ih / 2} textAnchor="middle" transform={`rotate(-90 14 ${T + ih / 2})`}
          style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-ink-2') }}>ANNUAL BENEFIT</text>
        {live.map((o) => {
          const r = 7 + Math.sqrt(Math.abs(o.npv) / maxN) * 20;
          const col = o.band?.color_code || tok(root, '--ix-s2');
          return (
            <circle key={o.id} cx={x(o.cost)} cy={y(o.benefit)} r={r} fill={col}
              fillOpacity={o.npv < 0 ? 0.12 : 0.28} stroke={col}
              strokeWidth={o.npv < 0 ? 1.6 : 1.2} strokeDasharray={o.npv < 0 ? '3 2' : undefined}
              style={{ cursor: 'pointer' }}
              onMouseMove={(e) => show(e, (
                <><b>{o.name}</b>
                  <R k="Id" v={o.id} /><R k="Project" v={o.pid || '—'} />
                  <R k="Annual benefit" v={money(o.benefit)} /><R k="Build cost" v={money(o.cost)} />
                  <R k="Net annual" v={money(o.net)} />
                  <R k="Payback" v={o.payback ? o.payback.toFixed(1) + ' years' : 'never'} />
                  <R k="NPV" v={money(o.npv)} />
                  <R k="Score" v={o.score == null ? 'not scored' : o.score.toFixed(2)} />
                </>))}
              onMouseLeave={hide} />
          );
        })}
      </svg>
      <div className="ix-legend">
        <span>Circle area = NPV</span>
        <span>A dashed outline is a negative NPV</span>
        <span>Colour is the recommendation band</span>
      </div>
    </>
  );
}

/** Waterfall: gross benefit down to NPV. */
function Bridge({ opps, root, show, hide }: { opps: Opp[]; root: HTMLElement | null; show: Show; hide: () => void }) {
  const [W, H] = useChartBox(660, 320);
  if (!opps.length) return <div className="ix-empty">Nothing selected.</div>;
  const benefit = sum(opps, (o) => o.benefit) * HORIZON;
  const run = -sum(opps, (o) => o.opex) * HORIZON;
  const build = -sum(opps, (o) => o.cost);
  const npvTotal = sum(opps, (o) => o.npv);
  const discount = npvTotal - (benefit + run + build);
  const steps = [
    { l: 'Gross benefit', v: benefit, c: '--ix-good' },
    { l: 'Run-rate costs', v: run, c: '--ix-warn' },
    { l: 'Build cost', v: build, c: '--ix-crit' },
    { l: 'Discounting', v: discount, c: '--ix-s3' },
  ];
  if (!benefit && !build) return <div className="ix-empty">No financial figures recorded yet.</div>;
  const L = 56, Rr = 24, T = 22, B = 58, iw = W - L - Rr, ih = H - T - B;
  let acc = 0;
  const pts = steps.map((st) => { const from = acc; acc += st.v; return { ...st, from, to: acc }; });
  const all = [0, ...pts.map((p) => p.to), ...pts.map((p) => p.from)];
  const top = Math.max(...all) * 1.08 || 1;
  const bot = Math.min(0, ...all) * 1.1;
  const y = (v: number) => T + (1 - (v - bot) / (top - bot || 1)) * ih;
  const bw = iw / (steps.length + 1) - 16;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
      {[0, 1, 2, 3, 4].map((q) => { const v = bot + (top - bot) * q / 4;
        return (
          <g key={q}>
            <line x1={L} x2={L + iw} y1={y(v)} y2={y(v)} stroke={tok(root, '--ix-line-soft')} strokeWidth={1} />
            <text x={L - 8} y={y(v) + 3.5} textAnchor="end"
              style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-muted') }}>{money(v)}</text>
          </g>
        ); })}
      <line x1={L} x2={L + iw} y1={y(0)} y2={y(0)} stroke={tok(root, '--ix-line')} strokeWidth={1} />
      {pts.map((p, i) => {
        const xx = L + i * (iw / (steps.length + 1)) + 12;
        const y0 = y(Math.max(p.from, p.to)), y1 = y(Math.min(p.from, p.to));
        return (
          <g key={p.l}>
            <rect x={xx} y={y0} width={bw} height={Math.max(2, y1 - y0)} rx={2}
              fill={tok(root, p.c)} fillOpacity={0.85} style={{ cursor: 'pointer' }}
              onMouseMove={(e) => show(e, (<><b>{p.l}</b><R k="Amount" v={money(p.v)} /><R k="Running total" v={money(p.to)} /></>))}
              onMouseLeave={hide} />
            <text x={xx + bw / 2} y={y0 - 6} textAnchor="middle"
              style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-ink') }}>{money(p.v)}</text>
            <text x={xx + bw / 2} y={T + ih + 18} textAnchor="middle"
              style={{ font: "500 10.5px 'IBM Plex Sans',sans-serif", fill: tok(root, '--ix-ink-2') }}>{p.l}</text>
          </g>
        );
      })}
      {(() => {
        const xx = L + steps.length * (iw / (steps.length + 1)) + 12;
        return (
          <g>
            <rect x={xx} y={y(Math.max(0, acc))} width={bw} height={Math.max(2, Math.abs(y(acc) - y(0)))} rx={2}
              fill={acc >= 0 ? tok(root, '--ix-accent') : tok(root, '--ix-crit')} style={{ cursor: 'pointer' }}
              onMouseMove={(e) => show(e, (<><b>Portfolio NPV</b><R k="Over" v={`${HORIZON} years`} /><R k="Value" v={money(acc)} /></>))}
              onMouseLeave={hide} />
            <text x={xx + bw / 2} y={y(Math.max(0, acc)) - 6} textAnchor="middle"
              style={{ font: "700 11px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-ink') }}>{money(acc)}</text>
            <text x={xx + bw / 2} y={T + ih + 18} textAnchor="middle"
              style={{ font: "600 10.5px 'IBM Plex Sans',sans-serif", fill: tok(root, '--ix-ink') }}>Portfolio NPV</text>
          </g>
        );
      })()}
    </svg>
  );
}

/** Payback histogram. */
function Payback({ opps, root, show, hide }: { opps: Opp[]; root: HTMLElement | null; show: Show; hide: () => void }) {
  const [W, H] = useChartBox(540, 240);
  const B: [number, number, string][] = [[0, 1, '< 1 yr'], [1, 2, '1–2'], [2, 3, '2–3'], [3, 5, '3–5'], [5, 1e8, '5 yr +'], [1e8, 1e9, 'never']];
  const counts = B.map(([lo, hi]) => opps.filter((o) => { const p = o.payback == null ? 1e8 + 1 : o.payback; return p >= lo && p < hi; }).length);
  if (!counts.some((c) => c)) return <div className="ix-empty">Nothing to show.</div>;
  const L = 42, Rr = 16, T = 18, Bm = 48, iw = W - L - Rr, ih = H - T - Bm;
  const max = Math.max(...counts) || 1, bw = iw / B.length - 16;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
      {[0, 1, 2, 3, 4].map((q) => { const v = Math.round((max * q) / 4), yy = T + ih - (v / max) * ih;
        return (<g key={q}>
          <line x1={L} x2={L + iw} y1={yy} y2={yy} stroke={tok(root, '--ix-line-soft')} strokeWidth={1} />
          <text x={L - 7} y={yy + 3.5} textAnchor="end"
            style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-muted') }}>{v}</text>
        </g>); })}
      {B.map(([, , lbl], i) => {
        const xx = L + i * (iw / B.length) + 8, n = counts[i]!, h = (n / max) * ih;
        const col = i < 2 ? tok(root, '--ix-good') : i < 4 ? tok(root, '--ix-warn') : tok(root, '--ix-crit');
        return (
          <g key={lbl}>
            {n ? (
              <>
                <rect x={xx} y={T + ih - h} width={bw} height={h} rx={2} fill={col} style={{ cursor: 'pointer' }}
                  onMouseMove={(e) => show(e, (<><b>{`Payback ${lbl}`}</b><R k="Opportunities" v={n} /></>))}
                  onMouseLeave={hide} />
                <text x={xx + bw / 2} y={T + ih - h - 6} textAnchor="middle"
                  style={{ font: "600 11px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-ink') }}>{n}</text>
              </>
            ) : null}
            <text x={xx + bw / 2} y={T + ih + 16} textAnchor="middle"
              style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-muted') }}>{lbl}</text>
          </g>
        );
      })}
      <text x={L + iw / 2} y={H - 10} textAnchor="middle"
        style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-ink-2') }}>YEARS TO PAY BACK THE BUILD</text>
    </svg>
  );
}

/** Portfolio radar across the assessment dimensions. */
function Radar({ opps, root, show, hide }: { opps: Opp[]; root: HTMLElement | null; show: Show; hide: () => void }) {
  const [W, H] = useChartBox(520, 340);
  const scored = opps.filter((o) => o.score != null && Object.keys(o.dims).length);
  const names = [...new Set(scored.flatMap((o) => Object.keys(o.dims)))];
  if (names.length < 3) return <div className="ix-empty">Not enough dimension scores to draw a profile.</div>;
  const avg = names.map((nm) => {
    const vals = scored.map((o) => o.dims[nm]).filter((v): v is number => v != null);
    return vals.length ? sum(vals, (v) => v) / vals.length : 0;
  });
  /* The web sits left of centre so the legend beside it has room; both follow the width. */
  const k = names.length, cx = Math.min(W * 0.32, 260), cy = H / 2;
  const Rr = Math.max(80, Math.min(cx - 56, H / 2 - 32));
  const ang = (i: number) => (i / k) * 2 * Math.PI - Math.PI / 2;
  const at = (i: number, r: number) => [cx + r * Math.cos(ang(i)), cy + r * Math.sin(ang(i))] as const;
  const poly = (f: (i: number) => number) => Array.from({ length: k }, (_, i) => at(i, f(i)).join(',')).join(' ');
  const accent = tok(root, '--ix-accent'), line = tok(root, '--ix-line-soft');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
      {[1, 2, 3, 4, 5].map((r) => <polygon key={r} points={poly(() => (Rr * r) / 5)} fill="none" stroke={line} strokeWidth={1} />)}
      {Array.from({ length: k }, (_, i) => { const [px, py] = at(i, Rr);
        return <line key={i} x1={cx} y1={cy} x2={px} y2={py} stroke={line} strokeWidth={1} />; })}
      <polygon points={poly((i) => (Rr * Math.max(0, Math.min(5, avg[i]!))) / 5)} fill={accent}
        fillOpacity={0.22} stroke={accent} strokeWidth={2} strokeLinejoin="round" />
      {Array.from({ length: k }, (_, i) => { const [px, py] = at(i, (Rr * Math.max(0, Math.min(5, avg[i]!))) / 5);
        return <circle key={i} cx={px} cy={py} r={4.5} fill={accent} style={{ cursor: 'pointer' }}
          onMouseMove={(e) => show(e, (<><b>{names[i]}</b><R k="Average" v={`${avg[i]!.toFixed(2)} / 5`} /></>))}
          onMouseLeave={hide} />; })}
      {names.map((nm, i) => (
        <g key={nm}>
          <text x={330} y={58 + i * 44} style={{ font: "500 11.5px 'IBM Plex Sans',sans-serif", fill: tok(root, '--ix-ink-2') }}>{clip(nm, 22)}</text>
          <text x={330} y={75 + i * 44} style={{ font: "700 13px 'IBM Plex Mono',monospace", fill: accent }}>{avg[i]!.toFixed(2)}</text>
          <line x1={318} x2={322} y1={52 + i * 44} y2={79 + i * 44} stroke={accent} strokeWidth={2} />
        </g>
      ))}
      <text x={cx} y={H - 8} textAnchor="middle"
        style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-muted') }}>
        1 – 5 AVERAGE ACROSS {scored.length} SCORED
      </text>
    </svg>
  );
}

/** Score against ROI, sized by build cost. */
function ScoreValue({ opps, root, show, hide }: { opps: Opp[]; root: HTMLElement | null; show: Show; hide: () => void }) {
  const [W, H] = useChartBox(540, 360);
  const live = opps.filter((o) => o.score != null);
  if (!live.length) return <div className="ix-empty">Nothing in this selection has been scored.</div>;
  const L = 56, Rr = 18, T = 20, B = 48, iw = W - L - Rr, ih = H - T - B;
  const lo = Math.min(3, ...live.map((o) => o.score!)) - 0.2;
  const maxR = Math.max(10, ...live.map((o) => o.roi)) * 1.1;
  const x = (v: number) => L + ((v - lo) / (5 - lo)) * iw;
  const y = (v: number) => T + (1 - Math.min(v, maxR) / maxR) * ih;
  const maxC = Math.max(1, ...live.map((o) => o.cost));
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
        <rect x={x(4)} y={T} width={L + iw - x(4)} height={y(maxR / 2) - T} fill={tok(root, '--ix-good-soft')} opacity={0.7} />
        {[3.5, 4, 4.5, 5].filter((v) => v > lo).map((v) => (
          <g key={v}>
            <line x1={x(v)} x2={x(v)} y1={T} y2={T + ih} stroke={tok(root, '--ix-line-soft')} strokeWidth={1} />
            <text x={x(v)} y={H - 26} textAnchor="middle"
              style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-muted') }}>{v.toFixed(1)}</text>
          </g>
        ))}
        {[0, 1, 2, 3, 4].map((q) => (
          <g key={q}>
            <line x1={L} x2={L + iw} y1={y((maxR * q) / 4)} y2={y((maxR * q) / 4)} stroke={tok(root, '--ix-line-soft')} strokeWidth={1} />
            <text x={L - 7} y={y((maxR * q) / 4) + 3.5} textAnchor="end"
              style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-muted') }}>{Math.round((maxR * q) / 4)}%</text>
          </g>
        ))}
        <text x={L + iw / 2} y={H - 9} textAnchor="middle"
          style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-ink-2') }}>ASSESSMENT SCORE →</text>
        <text x={13} y={T + ih / 2} textAnchor="middle" transform={`rotate(-90 13 ${T + ih / 2})`}
          style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-ink-2') }}>ROI</text>
        <text x={L + iw - 6} y={T + 14} textAnchor="end"
          style={{ font: "600 9.5px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-good'), letterSpacing: '.07em' }}>
          HIGH MERIT, HIGH RETURN
        </text>
        {live.map((o) => {
          const r = 6 + Math.sqrt(o.cost / maxC) * 16;
          const col = o.band?.color_code || tok(root, '--ix-s3');
          return <circle key={o.id} cx={x(o.score!)} cy={y(o.roi)} r={r} fill={col} fillOpacity={0.3}
            stroke={col} strokeWidth={1.3} style={{ cursor: 'pointer' }}
            onMouseMove={(e) => show(e, (
              <><b>{o.name}</b><R k="Score" v={o.score!.toFixed(2)} /><R k="ROI" v={pctTxt(o.roi)} />
                <R k="Build cost" v={money(o.cost)} /><R k="Band" v={o.band?.label ?? '—'} /></>))}
            onMouseLeave={hide} />;
        })}
      </svg>
      <div className="ix-legend"><span>Circle area = build cost</span></div>
    </>
  );
}

/** ROI with its best and worst case as a floating bar. */
function RoiRange({ opps, root, show, hide }: { opps: Opp[]; root: HTMLElement | null; show: Show; hide: () => void }) {
  const W = useChartW(640);
  const live = opps.filter((o) => o.hasModel).sort((a, b) => b.roi - a.roi).slice(0, 14);
  if (!live.length) return <div className="ix-empty">No opportunity in this selection has a financial model yet.</div>;
  const rowH = 26, L = 210, Rr = 70, H = 22 + live.length * rowH;
  const lo = Math.min(0, ...live.map((o) => o.roiWorst ?? o.roi));
  const hi = Math.max(...live.map((o) => o.roiBest ?? o.roi)) * 1.05 || 1;
  const x = (v: number) => L + ((v - lo) / (hi - lo || 1)) * (W - L - Rr);
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
        <line x1={x(0)} x2={x(0)} y1={12} y2={H - 6} stroke={tok(root, '--ix-ink-2')} strokeWidth={1.2} strokeDasharray="3 3" />
        <text x={x(0)} y={9} textAnchor="middle" style={{ font: "600 9.5px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-muted') }}>0%</text>
        {live.map((o, i) => {
          const yy = 16 + i * rowH;
          const col = o.roi >= 100 ? tok(root, '--ix-good') : o.roi >= 0 ? tok(root, '--ix-warn') : tok(root, '--ix-crit');
          return (
            <g key={o.id} style={{ cursor: 'pointer' }}
              onMouseMove={(e) => show(e, (
                <><b>{o.name}</b><R k="Expected ROI" v={pctTxt(o.roi)} />
                  <R k="Best case" v={o.roiBest == null ? '—' : pctTxt(o.roiBest)} />
                  <R k="Worst case" v={o.roiWorst == null ? '—' : pctTxt(o.roiWorst)} />
                  <R k="Payback" v={o.payback ? o.payback.toFixed(1) + 'y' : 'never'} /></>))}
              onMouseLeave={hide}>
              <text x={0} y={yy + 13} style={{ font: "500 11px 'IBM Plex Sans',sans-serif", fill: tok(root, '--ix-ink-2') }}>{clip(o.name, 26)}</text>
              {o.roiWorst != null && o.roiBest != null ? (
                <line x1={x(o.roiWorst)} x2={x(o.roiBest)} y1={yy + 9} y2={yy + 9} stroke={col}
                  strokeWidth={3} strokeOpacity={0.3} strokeLinecap="round" />
              ) : null}
              <circle cx={x(o.roi)} cy={yy + 9} r={5} fill={col} />
              <text x={W - Rr + 8} y={yy + 13} style={{ font: "600 10.5px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-ink') }}>{Math.round(o.roi)}%</text>
            </g>
          );
        })}
      </svg>
      <div className="ix-legend">
        <span><i className="ix-sw" style={{ background: 'var(--ix-good)', borderRadius: '50%' }} />Expected</span>
        <span>The bar spans the best and worst case the model carries</span>
      </div>
    </>
  );
}

/**
 * CONCENTRATION — a squarified treemap over a Pareto strip.
 *
 * The first version sliced the rectangle in halves, which is quick but produces slivers:
 * a 6%%-share group came out 12px wide and 240px tall, carried no label, and said nothing
 * about concentration. This uses the standard SQUARIFIED layout — grow a strip while the
 * worst aspect ratio in it is still improving, then lay it down — so every cell is close
 * to square and large enough to label.
 *
 * Fill is a SEQUENTIAL ramp on one hue, darkest for the largest share, because the thing
 * being encoded is magnitude and the cells are already ordered by it. A categorical set of
 * hues would imply the groups differ in kind rather than in size. Ink flips from white to
 * dark at the step where white stops clearing 4.5:1 — the ramp is measured, not guessed.
 *
 * The strip beneath is the concentration itself, which a treemap only implies: shares in
 * rank order with the cumulative curve over them, and the caption states where the top
 * groups land. That is the sentence an executive reads off this panel.
 */
function Treemap({ rows, root, show, hide }: {
  rows: { k: string; v: number; n: number; cost: number }[]; root: HTMLElement | null; show: Show; hide: () => void;
}) {
  const [W, boxH] = useChartBox(540, 300);
  const live = rows.filter((r) => r.v > 0).sort((a, b) => b.v - a.v);
  if (!live.length) return <div className="ix-empty">No benefit recorded for this selection.</div>;

  const total = sum(live, (r) => r.v);
  const STRIP = 54;                               /* Pareto strip + its axis labels */
  const H = Math.max(200, boxH - STRIP);
  const RAMP = ['--ix-s6', '--ix-s5', '--ix-s4', '--ix-s3', '--ix-s2', '--ix-s1'];
  const step = (i: number) => Math.min(RAMP.length - 1, i);
  /* White clears 4.5:1 on the three darkest steps only: 10.9, 7.0, 5.3 — then 3.4 at s3. */
  const inkOf = (i: number) => (step(i) <= 2 ? '#ffffff' : '#14202a');

  type Cell = { r: typeof live[number]; i: number; x: number; y: number; w: number; h: number };
  const cells: Cell[] = [];
  {
    let rest = live.map((r, i) => ({ r, i }));
    let x = 0, y = 0, w = W, h = H, remaining = total;
    while (rest.length && w > 1 && h > 1 && remaining > 0) {
      if (rest.length === 1) {
        cells.push({ ...rest[0]!, x, y, w, h });
        break;
      }
      const side = Math.min(w, h);
      const area = (v: number) => (v / remaining) * w * h;
      const worst = (grp: typeof rest) => {
        const s = sum(grp, (g) => area(g.r.v));
        if (s <= 0) return Infinity;
        const mx = Math.max(...grp.map((g) => area(g.r.v)));
        const mn = Math.min(...grp.map((g) => area(g.r.v)));
        return Math.max((side * side * mx) / (s * s), (s * s) / (side * side * mn));
      };
      const strip = [rest[0]!];
      let n = 1;
      while (n < rest.length && worst([...strip, rest[n]!]) <= worst(strip)) strip.push(rest[n++]!);
      const stripArea = sum(strip, (g) => area(g.r.v));
      const thick = stripArea / side;
      let off = 0;
      strip.forEach((g) => {
        const len = (area(g.r.v) / stripArea) * side;
        if (w >= h) cells.push({ ...g, x, y: y + off, w: thick, h: len });
        else cells.push({ ...g, x: x + off, y, w: len, h: thick });
        off += len;
      });
      if (w >= h) { x += thick; w -= thick; } else { y += thick; h -= thick; }
      remaining -= sum(strip, (g) => g.r.v);
      rest = rest.slice(strip.length);
    }
  }

  /* The Pareto strip: shares in rank order, with the cumulative curve over them. */
  const sy = H + 16, sh = 13;
  let run = 0;
  const bars = live.map((r, i) => {
    const x0 = (run / total) * W;
    run += r.v;
    return { r, i, x: x0, w: (r.v / total) * W, cum: run / total };
  });
  const topN = Math.min(3, live.length);
  const topShare = (sum(live.slice(0, topN), (r) => r.v) / total) * 100;
  const half = bars.findIndex((b) => b.cum >= 0.5) + 1;
  const curve = bars.map((b) => `${b.x + b.w},${sy - 4 - b.cum * (sh + 10)}`).join(' ');
  const muted = tok(root, '--ix-muted');

  return (
    <svg viewBox={`0 0 ${W} ${sy + sh + 22}`} width={W} height={sy + sh + 22} role="img">
        {cells.map(({ r, i, x, y, w, h }) => {
          const fill = tok(root, RAMP[step(i)]!);
          const ink = inkOf(i);
          const share = (r.v / total) * 100;
          return (
            <g key={r.k} style={{ cursor: 'pointer' }}
              onMouseMove={(e) => show(e, (
                <><b>{r.k}</b>
                  <R k="Annual benefit" v={money(r.v)} />
                  <R k="Build cost" v={money(r.cost)} />
                  <R k="Opportunities" v={r.n} />
                  <R k="Share of benefit" v={pctTxt(share)} /></>))}
              onMouseLeave={hide}>
              {/* A 2px surface gap, so neighbouring fills read as separate cells. */}
              <rect x={x + 1} y={y + 1} width={Math.max(1, w - 2)} height={Math.max(1, h - 2)}
                rx={4} fill={fill} />
              {w > 74 && h > 34 ? (
                <>
                  <text x={x + 9} y={y + 19}
                    style={{ font: "600 11.5px 'IBM Plex Sans',sans-serif", fill: ink }}>
                    {clip(r.k, Math.floor((w - 16) / 6.4))}
                  </text>
                  <text x={x + 9} y={y + 34}
                    style={{ font: "700 12px 'IBM Plex Mono',monospace", fill: ink, fillOpacity: 0.92 }}>
                    {money(r.v)}
                  </text>
                  {h > 56 ? (
                    <text x={x + 9} y={y + 49}
                      style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: ink, fillOpacity: 0.7 }}>
                      {pctTxt(share)} · {r.n} opportunit{r.n === 1 ? 'y' : 'ies'}
                    </text>
                  ) : null}
                </>
              ) : w > 30 && h > 18 ? (
                <text x={x + w / 2} y={y + h / 2 + 3.5} textAnchor="middle"
                  style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: ink, fillOpacity: 0.85 }}>
                  {pctTxt(share)}
                </text>
              ) : null}
            </g>
          );
        })}

        {/* ── Pareto strip ───────────────────────────────────────────────────────── */}
        <polyline points={curve} fill="none" stroke={tok(root, '--ix-copper')} strokeWidth={2}
          strokeLinejoin="round" />
        {bars.map((b) => (
          <g key={b.r.k}>
            <rect x={b.x + 1} y={sy} width={Math.max(1, b.w - 2)} height={sh} rx={2.5}
              fill={tok(root, RAMP[step(b.i)]!)} />
            <circle cx={b.x + b.w} cy={sy - 4 - b.cum * (sh + 10)} r={2.6}
              fill={tok(root, '--ix-copper')} />
          </g>
        ))}
        <text x={0} y={sy + sh + 16}
          style={{ font: "500 10.5px 'IBM Plex Sans',sans-serif", fill: muted }}>
          Top {topN} of {live.length} hold {pctTxt(topShare)} of the benefit
          {half > 0 ? ` · half of it sits in ${half} group${half === 1 ? '' : 's'}` : ''}
        </text>
    </svg>
  );
}

/** Status funnel. */
function Funnel({ opps, root, show, hide }: { opps: Opp[]; root: HTMLElement | null; show: Show; hide: () => void }) {
  const W = useChartW(540);
  const S: [string, string][] = [['Draft', '--ix-s1'], ['Approved', '--ix-s2'],
    ['Ready for Scoring', '--ix-s3'], ['Scoring in Progress', '--ix-s4'], ['Scored', '--ix-good']];
  const counts = S.map(([st]) => opps.filter((o) => o.status === st).length);
  if (!counts.some((c) => c)) return <div className="ix-empty">Nothing to show.</div>;
  const L = 178, Rr = 60, T = 12, rowH = 46, H = T + S.length * rowH;
  const max = Math.max(...counts) || 1;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
      {S.map(([st, t], i) => {
        const yy = T + i * rowH, n = counts[i]!, bw = (W - L - Rr) * (n / max);
        return (
          <g key={st} style={{ cursor: 'pointer' }}
            onMouseMove={(e) => show(e, (
              <><b>{st}</b><R k="Opportunities" v={n} />
                <R k="Benefit" v={money(sum(opps.filter((o) => o.status === st), (o) => o.benefit))} /></>))}
            onMouseLeave={hide}>
            <rect x={L} y={yy} width={W - L - Rr} height={30} rx={3} fill={tok(root, '--ix-line-soft')} />
            <rect x={L} y={yy} width={Math.max(2, bw)} height={30} rx={3} fill={tok(root, t)} />
            <text x={0} y={yy + 20} style={{ font: "500 12px 'IBM Plex Sans',sans-serif", fill: tok(root, '--ix-ink-2') }}>{st}</text>
            <text x={W - Rr + 8} y={yy + 20} style={{ font: "700 13px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-ink') }}>{n}</text>
          </g>
        );
      })}
    </svg>
  );
}

/** Coverage grid — what each project actually has on file. */
function Coverage({ opps, projects, root, show, hide }: {
  opps: Opp[]; projects: ProjectRow[]; root: HTMLElement | null; show: Show; hide: () => void;
}) {
  const W = useChartW(680);
  const COLS: [string, (o: Opp) => boolean][] = [
    ['Scored', (o) => o.score != null],
    ['Financial model', (o) => o.hasModel],
    ['NPV', (o) => o.hasNpv],
    ['Pathway B', (o) => o.pathway === 'B'],
    ['Scoring relevant', (o) => o.relevant],
  ];
  const rows = projects.filter((p) => opps.some((o) => o.pid === p.project_id));
  if (!rows.length) return <div className="ix-empty">Nothing selected.</div>;
  const L = 250, Rr = 16, rowH = 34, T = 26, H = T + rows.length * rowH + 10;
  const cw = (W - L - Rr) / COLS.length - 5;
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
        {COLS.map(([c], i) => (
          <text key={c} x={L + i * (cw + 5) + cw / 2} y={15} textAnchor="middle"
            style={{ font: "500 9.5px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-muted') }}>{c.toUpperCase()}</text>
        ))}
        {rows.map((p, r) => {
          const mine = opps.filter((o) => o.pid === p.project_id);
          const yy = T + r * rowH;
          return (
            <g key={p.project_id}>
              <text x={0} y={yy + 19} style={{ font: "600 10.5px 'IBM Plex Mono',monospace", fill: tok(root, '--ix-ink-2') }}>{p.project_id}</text>
              <text x={54} y={yy + 19} style={{ font: "500 11.5px 'IBM Plex Sans',sans-serif", fill: tok(root, '--ix-ink-2') }}>
                {clip(p.project_description || p.project_id, 24)}
              </text>
              {COLS.map(([c, f], i) => {
                const n = mine.filter(f).length;
                const share = mine.length ? n / mine.length : 0;
                const col = share > 0.8 ? tok(root, '--ix-good') : share > 0.5 ? tok(root, '--ix-s3')
                  : share > 0.2 ? tok(root, '--ix-warn') : tok(root, '--ix-crit');
                const xx = L + i * (cw + 5);
                return (
                  <g key={c} style={{ cursor: 'pointer' }}
                    onMouseMove={(e) => show(e, (
                      <><b>{p.project_description || p.project_id}</b>
                        <R k={c} v={`${n} of ${mine.length}`} /><R k="Share" v={pctTxt(share * 100)} /></>))}
                    onMouseLeave={hide}>
                    <rect x={xx} y={yy + 5} width={cw} height={rowH - 12} rx={3} fill={col} fillOpacity={share ? 0.85 : 0.18} />
                    <text x={xx + cw / 2} y={yy + 19} textAnchor="middle"
                      style={{ font: "600 10.5px 'IBM Plex Mono',monospace", fill: share > 0.2 ? '#fff' : tok(root, '--ix-muted') }}>
                      {n}/{mine.length}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
      <div className="ix-legend">
        <span><i className="ix-sw" style={{ background: 'var(--ix-good)' }} />Over 80%</span>
        <span><i className="ix-sw" style={{ background: 'var(--ix-s3)' }} />50–80%</span>
        <span><i className="ix-sw" style={{ background: 'var(--ix-warn)' }} />20–50%</span>
        <span><i className="ix-sw" style={{ background: 'var(--ix-crit)' }} />Under 20%</span>
      </div>
    </>
  );
}

/* ── Help ───────────────────────────────────────────────────────────────────────────── */

const EXEC_HELP: HelpContent = {
  title: 'Executive Dashboard',
  icon: '💠',
  purpose:
    'One read across the whole registry — what the portfolio is worth, what it costs, how it ' +
    'scores and how much of it has actually been assessed and modelled — so an investment ' +
    'committee can find the two or three decisions worth making before reading any detail screen.',
  doHere: [
    'Pick the projects with the <strong>Project</strong> field. Everything on the screen recomputes.',
    'Use the <strong>filters</strong> — scoring relevant, scored, has a financial model, quick payback, negative NPV — to select by condition rather than by name. Each carries its count.',
    'Move between <strong>Value &amp; ROI</strong>, <strong>Scoring</strong>, <strong>Financial modelling</strong> and <strong>Pipeline &amp; coverage</strong>. Hover any mark for its underlying numbers.',
  ],
  fits: 'Portfolio Optimizer &rarr; <strong>Executive Dashboard</strong>',
  firstTime:
    'Read the value map first. The copper diagonals are one-, two- and three-year payback — anything above the top line pays for itself inside a year.',
  sections: [
    {
      label: 'Where the numbers come from',
      type: 'html',
      html:
        'Everything here is <strong>Opportunity Registry</strong>: opportunities and their business ' +
        'case, the project they belong to, the assessment framework and its dimension scores, the ' +
        'recommendation bands, and the financial model where one exists. <strong>Nothing is read ' +
        'from Project Management</strong> — no delivery programs, no work items, no phase tracking.' +
        '<br><br>Where an opportunity has a <strong>financial model</strong>, its figures are used: ' +
        'they are the worked case. Where it has none, the business case on the opportunity is used, ' +
        'because that is the only figure it has. Preferring the model everywhere would make an ' +
        'unmodelled opportunity read as worthless rather than as unmodelled — which is exactly what ' +
        'the coverage grid exists to show.',
    },
  ],
  links: [
    { label: 'Scoring Dashboard →', href: '/portfolio_definition/opportunities/scoring-dashboard' },
    { label: 'Portfolio Optimizer →', href: '/portfolio_definition/portfolio-optimizer/strategy-and-constraints' },
  ],
};

/* ════════════════════════════════════════════════════════════════════════════════════ */

type View = 'value' | 'scoring' | 'financial' | 'pipeline';
type FilterKey = 'relevant' | 'scored' | 'model' | 'fast' | 'negative';

export function ExecutiveDashboard() {
  /**
   * THE SCREEN IS MEASURED, NOT SIZED IN PERCENT.
   *
   * `height: 100%` resolves against nothing in this shell — `<main>` carries
   * `min-height: 100vh` and grows with its content — so the WINDOW scrolls and takes the
   * heading with it. Measured from the viewport instead, re-measured on resize and whenever
   * `<main>` changes, with a CALLBACK ref because the effect must re-run when the node
   * actually mounts. Same block Activity Timeline and the Project Management dashboard carry.
   */
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);
  useEffect(() => {
    if (!root) return;
    const main = root.closest('main');
    const measure = () => {
      const top = root.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => { window.removeEventListener('resize', measure); ro.disconnect(); };
  }, [root]);

  const { show, hide, node: tipNode } = useTip();
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>('value');
  const [picked, setPicked] = useState<string[]>([]);
  const [filter, setFilter] = useState<FilterKey | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const d = (await pdApi.executive.data()) as unknown as Payload;
        if (!d.success) { setError(d.error || 'Could not load the dashboard'); return; }
        setData(d);
        setPicked([
          ...d.projects.map((p) => p.project_id),
          ...(d.opportunities.some((o) => !o.project_id) ? [UNASSIGNED] : []),
        ]);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const all = useMemo(() => (data ? build(data) : []), [data]);

  const FILTERS: { key: FilterKey; label: string; test: (o: Opp) => boolean }[] = useMemo(() => [
    { key: 'relevant', label: 'Scoring relevant', test: (o) => o.relevant },
    { key: 'scored', label: 'Scored', test: (o) => o.score != null },
    { key: 'model', label: 'Has financial model', test: (o) => o.hasModel },
    { key: 'fast', label: 'Payback under 1 year', test: (o) => o.payback != null && o.payback < 1 },
    { key: 'negative', label: 'Negative NPV', test: (o) => o.npv < 0 },
  ], []);

  const inProjects = useMemo(() => all.filter((o) => picked.includes(o.pid || UNASSIGNED)), [all, picked]);
  const opps = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter);
    return f ? inProjects.filter(f.test) : inProjects;
  }, [inProjects, filter, FILTERS]);

  const projects = data?.projects ?? [];
  const benefit = sum(opps, (o) => o.benefit);
  const cost = sum(opps, (o) => o.cost);
  const net = benefit - sum(opps, (o) => o.opex);
  const npv = sum(opps, (o) => o.npv);
  const roi = cost ? (net / cost) * 100 : 0;
  const scored = opps.filter((o) => o.score != null).length;
  const modelled = opps.filter((o) => o.hasModel).length;

  const byBand = (data?.bands ?? []).map((b) => ({
    n: b.label, v: opps.filter((o) => o.band?.label === b.label).length, c: b.color_code || '#5291bd',
  }));
  const STATUSES: [string, string][] = [['Scored', '--ix-good'], ['Scoring in Progress', '--ix-s4'],
    ['Ready for Scoring', '--ix-s3'], ['Approved', '--ix-s2'], ['Draft', '--ix-s1']];

  const groupBy = (key: (o: Opp) => string) => {
    const g: Record<string, Opp[]> = {};
    opps.forEach((o) => (g[key(o)] ??= []).push(o));
    return Object.entries(g).map(([k, v]) => ({ k, v: sum(v, (x) => x.benefit), n: v.length, cost: sum(v, (x) => x.cost) }));
  };

  const gc = (v: number) => (v > 66 ? tok(root, '--ix-good') : v > 33 ? tok(root, '--ix-warn') : tok(root, '--ix-crit'));
  const tabs: [View, string][] = [
    ['value', 'Value & ROI'], ['scoring', 'Scoring'],
    ['financial', 'Financial modelling'], ['pipeline', 'Pipeline & coverage'],
  ];

  return (
    <div className="pd-screen ir-exec pd-screen--locked" ref={setRoot} style={rootH ? { height: rootH } : undefined}>
      <HelpPanel help={EXEC_HELP} />

      <div className="ix-head">
        <div className="ix-titlerow">
          <i className="material-icons ix-titleicon">query_stats</i>
          <div>
            <h1 className="pd-page-header__title">Executive Dashboard</h1>
            <p className="ix-sub">
              What the portfolio is worth, what it costs, how it scores, and how much of it has
              actually been assessed.
            </p>
          </div>
          <div className="ix-asof">
            <b>{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</b>
            {data
              ? `${all.length} opportunit${all.length === 1 ? 'y' : 'ies'} in the registry`
              : error ? 'registry unavailable' : 'reading the registry…'}
          </div>
        </div>

        <div className="ix-toolbar">
          <SelectField
            style={{ alignItems: 'center' }}
            label="Project"
            width="lg"
            id="ir-exec-project"
            multiple
            allLabel="All Projects"
            placeholder={PROMPT.project}
            value={picked}
            onChange={(v) => setPicked(v.length ? v : picked)}
            options={[
              ...projects.map((p) => ({
                value: p.project_id, id: p.project_id, description: p.project_description,
              })),
              ...(all.some((o) => !o.pid)
                ? [{ value: UNASSIGNED, id: 'Unassigned',
                     description: 'Opportunities not yet assigned to a project' }]
                : []),
            ]}
          />
          <div className="ix-chips" style={{ marginLeft: 'auto' }}>
            {FILTERS.map((f) => {
              const hits = inProjects.filter(f.test).length;
              return (
                <button key={f.key} type="button" className={`ix-qf${filter === f.key ? ' on' : ''}`}
                  disabled={!hits} aria-pressed={filter === f.key}
                  title={hits ? `Show only the ${hits} matching` : 'Nothing matches'}
                  onClick={() => setFilter(filter === f.key ? null : f.key)}>
                  {f.label}<span className="n">{hits}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="ix-tabs" role="tablist">
          {tabs.map(([k, label]) => (
            <button key={k} type="button" role="tab" className="ix-tab" aria-selected={view === k}
              onClick={() => setView(k)}>{label}</button>
          ))}
        </div>
      </div>

      <div className="ix-body">
        {error ? (
          <div className="ix-empty"><b>{error}</b></div>
        ) : loading ? (
          <div className="ix-empty">Loading…</div>
        ) : !all.length ? (
          <div className="ix-empty">There are no opportunities in the registry yet.</div>
        ) : (
          <div className="ix-layout">
            <aside className="ix-rail">
              <div className="ix-card">
                <div className="ix-card-hd"><div className="eyebrow">Portfolio health</div></div>
                <div className="ix-card-bd">
                  <div className="ix-gauges">
                    <Gauge value={opps.length ? (scored / opps.length) * 100 : 0} label="Assessed" colour={gc(opps.length ? (scored / opps.length) * 100 : 0)} />
                    <Gauge value={opps.length ? (modelled / opps.length) * 100 : 0} label="Modelled" colour={gc(opps.length ? (modelled / opps.length) * 100 : 0)} />
                    <Gauge value={benefit ? (net / benefit) * 100 : 0} label="Net margin" colour={tok(root, '--ix-accent')} />
                  </div>
                  <div className="ix-ft">
                    <div className="ix-ft-row"><span>Opportunities</span><b>{opps.length}</b></div>
                    <div className="ix-ft-row"><span>Annual benefit</span><b>{money(benefit)}</b></div>
                    <div className="ix-ft-row"><span>Build cost</span><b>{money(cost)}</b></div>
                    <div className="ix-ft-row"><span>Net annual</span><b>{money(net)}</b></div>
                    <div className="ix-ft-row"><span>Portfolio NPV</span><b>{money(npv)}</b></div>
                  </div>
                </div>
              </div>

              <div className="ix-card">
                <div className="ix-card-hd"><div className="eyebrow">Recommendation</div>
                  <span style={{ font: "500 10px 'IBM Plex Mono',monospace", color: 'var(--ix-muted)' }}>
                    {scored ? `${scored} scored` : ''}
                  </span>
                </div>
                <div className="ix-card-bd"><Stack parts={byBand} empty="Nothing scored in this selection." /></div>
              </div>

              <div className="ix-card">
                <div className="ix-card-hd"><div className="eyebrow">Assessment</div></div>
                <div className="ix-card-bd">
                  <Stack parts={STATUSES.map(([st, t]) => ({ n: st, v: opps.filter((o) => o.status === st).length, c: tok(root, t) }))} />
                </div>
              </div>

              <div className="ix-card">
                <div className="ix-card-hd"><div className="eyebrow">Biggest bets</div></div>
                <div className="ix-card-bd">
                  <div className="ix-ft" style={{ borderTop: 0, marginTop: 0 }}>
                    {opps.slice().sort((a, b) => b.cost - a.cost).slice(0, 5).map((o) => (
                      <div className="ix-ft-row" key={o.id}><span>{clip(o.name, 20)}</span><b>{money(o.cost)}</b></div>
                    ))}
                  </div>
                </div>
              </div>
            </aside>

            <div className="ix-main">
              <div className="ix-kpis">
                <Kpi label="Annual benefit" value={money(benefit)} sub={`${opps.length} opportunities`}
                  graphic={<MiniBars values={opps.slice().sort((a, b) => b.benefit - a.benefit).slice(0, 8).map((o) => o.benefit)} colour={tok(root, '--ix-accent')} />} />
                <Kpi label="Build cost" value={money(cost)} sub="one-off investment"
                  graphic={<MiniBars values={opps.slice().sort((a, b) => b.cost - a.cost).slice(0, 8).map((o) => o.cost)} colour={tok(root, '--ix-s3')} />} />
                <Kpi label="Net annual" value={money(net)} delta={benefit ? pctTxt((net / benefit) * 100) + ' of benefit' : ''}
                  deltaCol={tok(root, '--ix-good')} sub="after run-rate costs"
                  graphic={<MiniBars values={opps.slice().sort((a, b) => b.net - a.net).slice(0, 8).map((o) => Math.max(0, o.net))} colour={tok(root, '--ix-good')} />} />
                <Kpi label="Portfolio ROI" value={cost ? Math.round(roi) + '%' : '—'}
                  delta={net > 0 && cost ? (cost / net).toFixed(1) + 'y payback' : ''}
                  deltaCol={roi > 100 ? tok(root, '--ix-good') : tok(root, '--ix-warn')} sub="net annual over build cost"
                  graphic={<MiniBars values={opps.slice().sort((a, b) => b.roi - a.roi).slice(0, 8).map((o) => Math.max(0, o.roi))} colour={tok(root, '--ix-good')} />} />
                <Kpi label={`NPV, ${HORIZON} years`} value={money(npv)} delta={pctTxt(DISCOUNT * 100) + ' discount'}
                  deltaCol={npv > 0 ? tok(root, '--ix-good') : tok(root, '--ix-crit')} sub="net present value"
                  graphic={<MiniBars values={byBand.map((b) => b.v)} colour={tok(root, '--ix-accent')} />} />
              </div>

              {view === 'value' ? (
                <div className="ix-grid">
                  <Panel eyebrow="Where the value is" title="The value map"
                    note="Every opportunity placed by what it costs to build against what it returns each year, sized by NPV and coloured by the recommendation it earned. The copper diagonals are one-, two- and three-year payback.">
                    <ValueMap opps={opps} root={root} show={show} hide={hide} />
                  </Panel>
                  <div className="ix-grid ix-g2">
                    <Panel eyebrow="Bridge" title="From gross benefit to net present value"
                      note={`${HORIZON} years of benefit, less the run-rate costs it brings with it, less the one-off build, less the cost of money.`}>
                      <Bridge opps={opps} root={root} show={show} hide={hide} />
                    </Panel>
                    <Panel eyebrow="Return" title="How quickly the build pays back"
                      note="Every opportunity by years to recover its build cost from net annual benefit.">
                      <Payback opps={opps} root={root} show={show} hide={hide} />
                    </Panel>
                  </div>
                  <Panel eyebrow="Concentration" title="Where the benefit sits"
                    note="Annual benefit by business process, sized by share.">
                    <Treemap rows={groupBy((o) => o.l1)} root={root} show={show} hide={hide} />
                  </Panel>
                </div>
              ) : null}

              {view === 'scoring' ? (
                <div className="ix-grid">
                  <div className="ix-grid ix-g2">
                    <Panel eyebrow="Merit" title="How the portfolio scores by dimension"
                      note="The average of every scored opportunity on each assessment dimension. A lopsided figure is a portfolio strong in one respect and weak in another.">
                      <Radar opps={opps} root={root} show={show} hide={hide} />
                    </Panel>
                    <Panel eyebrow="Merit against money" title="Score versus return"
                      note="Assessment score against ROI, sized by build cost. The shaded corner is what you would fund first.">
                      <ScoreValue opps={opps} root={root} show={show} hide={hide} />
                    </Panel>
                  </div>
                  <Panel eyebrow="Ranking" title="Scored opportunities"
                    note="Every scored opportunity with its band, score and economics.">
                    <ScoredTable opps={opps} root={root} />
                  </Panel>
                </div>
              ) : null}

              {view === 'financial' ? (
                <div className="ix-grid">
                  <Panel eyebrow="Uncertainty" title="ROI with its best and worst case"
                    note="The dot is the expected return; the bar spans the best and worst case the financial model carries. A wide bar is a case that has not been pinned down.">
                    <RoiRange opps={opps} root={root} show={show} hide={hide} />
                  </Panel>
                  <Panel eyebrow="Total cost of ownership" title="What each opportunity costs to own"
                    note="Build cost plus the run rate it introduces, over three and five years, against the benefit and the NPV.">
                    <TcoTable opps={opps} root={root} />
                  </Panel>
                </div>
              ) : null}

              {view === 'pipeline' ? (
                <div className="ix-grid">
                  <div className="ix-grid ix-g2">
                    <Panel eyebrow="Pipeline" title="Where opportunities have got to"
                      note="Every opportunity by assessment status.">
                      <Funnel opps={opps} root={root} show={show} hide={hide} />
                    </Panel>
                    <Panel eyebrow="Concentration" title="Benefit by project"
                      note="Annual benefit by project, sized by share.">
                      <Treemap
                        rows={groupBy((o) => projects.find((p) => p.project_id === o.pid)?.project_description || o.pid || 'Unassigned')}
                        root={root} show={show} hide={hide} />
                    </Panel>
                  </div>
                  <Panel eyebrow="Coverage" title="What each project actually has on file"
                    note="How much of each project has been scored, modelled and routed. A red cell is a project that cannot be compared with the others yet.">
                    <Coverage opps={opps} projects={projects} root={root} show={show} hide={hide} />
                  </Panel>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
      {tipNode}
    </div>
  );
}

function Kpi({ label, value, delta, deltaCol, sub, graphic }: {
  label: string; value: string; delta?: string; deltaCol?: string; sub: string; graphic?: ReactNode;
}) {
  return (
    <div className="ix-kpi">
      <div className="eyebrow">{label}</div>
      <div className="v"><b>{value}</b>{delta ? <span className="d" style={{ color: deltaCol }}>{delta}</span> : null}</div>
      {graphic ? <div className="ix-spark">{graphic}</div> : null}
      <small>{sub}</small>
    </div>
  );
}

function ScoredTable({ opps, root }: { opps: Opp[]; root: HTMLElement | null }) {
  const rows = opps.filter((o) => o.score != null).sort((a, b) => b.score! - a.score!);
  if (!rows.length) return <div className="ix-empty">Nothing scored in this selection.</div>;
  return (
    <table>
      <thead><tr>
        <th>Opportunity</th><th>Project</th><th style={{ textAlign: 'right' }}>Score</th><th>Band</th>
        <th style={{ textAlign: 'right' }}>Benefit / yr</th><th style={{ textAlign: 'right' }}>Build</th>
        <th style={{ textAlign: 'right' }}>ROI</th><th style={{ textAlign: 'right' }}>NPV</th>
      </tr></thead>
      <tbody>
        {rows.map((o) => (
          <tr key={o.id}>
            <td><span className="ix-oid">{o.id}</span><span style={{ display: 'block', lineHeight: 1.35 }}>{esc(o.name)}</span></td>
            <td style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11 }}>{o.pid || '—'}</td>
            <td className="num"><b>{o.score!.toFixed(2)}</b></td>
            <td>{o.band ? (
              <span className="ix-chip" style={{ background: `${o.band.color_code}22`, color: o.band.color_code }}>{o.band.label}</span>
            ) : '—'}</td>
            <td className="num">{money(o.benefit)}</td>
            <td className="num">{money(o.cost)}</td>
            <td className="num">{Math.round(o.roi)}%</td>
            <td className="num" style={{ color: o.npv < 0 ? tok(root, '--ix-crit') : tok(root, '--ix-good') }}>{money(o.npv)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TcoTable({ opps, root }: { opps: Opp[]; root: HTMLElement | null }) {
  const rows = opps.slice().sort((a, b) => b.tco5 - a.tco5).slice(0, 15);
  if (!rows.length) return <div className="ix-empty">Nothing selected.</div>;
  return (
    <table>
      <thead><tr>
        <th>Opportunity</th><th>Project</th>
        <th style={{ textAlign: 'right' }}>Build</th><th style={{ textAlign: 'right' }}>Run / yr</th>
        <th style={{ textAlign: 'right' }}>TCO 3 yr</th><th style={{ textAlign: 'right' }}>TCO 5 yr</th>
        <th style={{ textAlign: 'right' }}>Benefit / yr</th><th style={{ textAlign: 'right' }}>NPV</th>
        <th style={{ textAlign: 'right' }}>Payback</th>
      </tr></thead>
      <tbody>
        {rows.map((o) => (
          <tr key={o.id}>
            <td><span className="ix-oid">{o.id}</span><span style={{ display: 'block', lineHeight: 1.35 }}>{esc(o.name)}</span></td>
            <td style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11 }}>{o.pid || '—'}</td>
            <td className="num">{money(o.cost)}</td>
            <td className="num">{money(o.opex)}</td>
            <td className="num">{money(o.tco3)}</td>
            <td className="num">{money(o.tco5)}</td>
            <td className="num">{money(o.benefit)}</td>
            <td className="num" style={{ color: o.npv < 0 ? tok(root, '--ix-crit') : tok(root, '--ix-good') }}>{money(o.npv)}</td>
            <td className="num">{o.payback ? o.payback.toFixed(1) + 'y' : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
